import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Popover,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FlagIcon from '@mui/icons-material/Flag';
import CheckIcon from '@mui/icons-material/Check';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { BackupsDialog } from './BackupsDialog';
import { Link as RouterLink } from 'react-router-dom';
import type { ProjectDeliveryMilestone, ProjectTrack, ScheduleDependency, ScheduleNodeKind, TrackItem, TrackItemDependency } from '../../api/types';
import {
  useAddDependency,
  useAddHoliday,
  useAddTrackItem,
  useCreateMilestone,
  useCreateMilestoneLink,
  useCreateTrack,
  useDeleteMilestone,
  useDeleteTrack,
  useDependencies,
  useHolidays,
  useMilestoneLinks,
  useProjectDashboard,
  useRemoveDependency,
  useRemoveHoliday,
  useRemoveMilestoneLink,
  useSchedule,
  useSetBaseline,
  useTrackTypes,
  useUpdateDeliveryMilestone,
  useUpdateDependencyLag,
  useUpdateItemPlan,
  useUpdateMilestoneLinkLag,
  useUpdateTrack,
} from '../../hooks/useOps';
import { formatDate, humanizeStatus, milestoneEffectiveDate, statusColor } from '../../lib/format';

const LABEL_MAX = 24;
const truncate = (s: string) => (s.length > LABEL_MAX ? `${s.slice(0, LABEL_MAX - 1)}…` : s);

/**
 * When the backend rejects a dependency as a cycle, show WHY: the existing
 * predecessor chain from the proposed successor back to the proposed
 * predecessor, rendered with item names. BFS over the current edge list.
 */
function findPath(
  fromId: string,
  toId: string,
  edges: TrackItemDependency[],
): string[] | null {
  const next = new Map<string, string[]>();
  for (const e of edges) {
    next.set(e.predecessorId, [...(next.get(e.predecessorId) ?? []), e.successorId]);
  }
  const queue: string[][] = [[fromId]];
  const seen = new Set<string>([fromId]);
  while (queue.length) {
    const path = queue.shift()!;
    const last = path[path.length - 1];
    if (last === toId) return path;
    for (const n of next.get(last) ?? []) {
      if (!seen.has(n)) {
        seen.add(n);
        queue.push([...path, n]);
      }
    }
  }
  return null;
}

/** Everything that defines the plan: tracks, items, dates, dependencies, baseline, gates, holidays. */
export function PlanTab({ projectId }: { projectId: string }) {
  const { data } = useProjectDashboard(projectId);
  const { data: trackTypes = [] } = useTrackTypes();
  const { data: dependencies = [] } = useDependencies(projectId);

  const [addingTrack, setAddingTrack] = useState<string | null>(null);
  const [trackForm, setTrackForm] = useState({
    label: '',
    unitLabel: 'pcs',
    totalUnits: '100',
    dueDate: '',
    plannedStart: '',
    plannedEnd: '',
  });
  const [addingItem, setAddingItem] = useState<string | null>(null);
  const [itemForm, setItemForm] = useState({ name: '', totalUnits: '100' });
  const [showMsForm, setShowMsForm] = useState(false);
  const [msForm, setMsForm] = useState({ name: '', dueDate: '' });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [confirmRebaseline, setConfirmRebaseline] = useState(false);
  const [backupsOpen, setBackupsOpen] = useState(false);

  const createTrack = useCreateTrack(projectId);
  const updateTrack = useUpdateTrack(projectId);
  const addTrackItem = useAddTrackItem(projectId);
  const deleteTrack = useDeleteTrack(projectId);
  const createMilestone = useCreateMilestone(projectId);
  const deleteMilestone = useDeleteMilestone(projectId);
  const updateDeliveryMilestone = useUpdateDeliveryMilestone(projectId);
  const updateItemPlan = useUpdateItemPlan(projectId);
  const addDependency = useAddDependency(projectId);
  const updateDependencyLag = useUpdateDependencyLag(projectId);
  const removeDependency = useRemoveDependency(projectId);
  const setBaseline = useSetBaseline(projectId);

  if (!data) return null;
  const { tracks, deliveryMilestones } = data;
  const existingTrackTypes = new Set(tracks.map((t) => t.type));

  const allItems = tracks.flatMap((t) =>
    [...(t.items ?? [])]
      .sort((a, b) => a.sequence - b.sequence)
      .map((i) => ({ id: i.id, label: `${t.label} › ${i.name}` })),
  );
  const itemLabel = (itemId: string) => allItems.find((i) => i.id === itemId)?.label ?? '(deleted item)';

  const baselineSet = tracks.some((t) => (t.items ?? []).some((i) => i.baselineStart));

  const handleCreateTrack = async () => {
    if (!trackForm.label || !addingTrack) return;
    try {
      await createTrack.mutateAsync({
        projectId,
        type: addingTrack,
        label: trackForm.label,
        unitLabel: trackForm.unitLabel,
        totalUnits: parseInt(trackForm.totalUnits, 10),
        dueDate: trackForm.dueDate || null,
        plannedStart: trackForm.plannedStart || null,
        plannedEnd: trackForm.plannedEnd || null,
      });
      setAddingTrack(null);
      setTrackForm({ label: '', unitLabel: 'pcs', totalUnits: '100', dueDate: '', plannedStart: '', plannedEnd: '' });
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Failed to create track');
    }
  };

  const handleAddItem = async (trackId: string) => {
    if (!itemForm.name) return;
    try {
      await addTrackItem.mutateAsync({
        trackId,
        body: { name: itemForm.name, totalUnits: parseInt(itemForm.totalUnits, 10) },
      });
      setAddingItem(null);
      setItemForm({ name: '', totalUnits: '100' });
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Failed to add item');
    }
  };

  /** Add a predecessor from an item row (W4). Cycle rejections explain the chain. */
  const handleAddPredecessor = async (successorId: string, predecessorId: string) => {
    try {
      await addDependency.mutateAsync({ predecessorId, successorId, lagDays: 0 });
    } catch (e) {
      const msg = (e as Error).message ?? 'Failed to add dependency';
      if (/cycle/i.test(msg)) {
        const path = findPath(successorId, predecessorId, dependencies);
        const chain = path ? path.map(itemLabel).join(' → ') : null;
        setErrorMsg(
          chain
            ? `Would create a cycle: ${chain} already leads back to ${itemLabel(successorId)}. Remove one of those links first.`
            : msg,
        );
      } else {
        setErrorMsg(msg);
      }
    }
  };

  const handleSetBaseline = async () => {
    if (baselineSet && !confirmRebaseline) {
      setConfirmRebaseline(true);
      return;
    }
    setConfirmRebaseline(false);
    try {
      const res = await setBaseline.mutateAsync();
      setOkMsg(
        `Baseline ${res.rebaselined ? 're-set' : 'set'} — ${res.itemsBaselined} planned item(s) snapshotted.`,
      );
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Failed to set baseline');
    }
  };

  const handleCreateMilestone = async () => {
    if (!msForm.name || !msForm.dueDate) return;
    try {
      await createMilestone.mutateAsync({
        projectId,
        name: msForm.name,
        dueDate: msForm.dueDate,
      });
      setMsForm({ name: '', dueDate: '' });
      setShowMsForm(false);
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Failed to add milestone');
    }
  };

  const handleCompleteGate = async (id: string) => {
    try {
      await updateDeliveryMilestone.mutateAsync({
        id,
        body: { completedDate: new Date().toISOString().slice(0, 10) },
      });
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Failed to complete milestone');
    }
  };

  return (
    <Stack gap={2}>
      {/* Tracks + items + planned dates */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              Tracks
            </Typography>
            <Tooltip title="Planned dates are your input; the engine derives scheduled dates from dependencies and holidays; the baseline is a frozen snapshot for drift comparison (Gantt ghost bar).">
              <Chip size="small" variant="outlined" label="planned vs scheduled vs baseline?" />
            </Tooltip>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            {baselineSet && <Chip size="small" color="info" variant="outlined" label="Baseline set" />}
            <Tooltip title="Save full project backups and restore (roll back) the whole project to a saved state">
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsBackupRestoreIcon />}
                onClick={() => setBackupsOpen(true)}
              >
                Backups
              </Button>
            </Tooltip>
            <Tooltip title="Snapshot the current schedule (scheduled dates, or planned where unscheduled) as the frozen baseline — the Gantt ghost bar. Re-set it to realign the ghost bar to the current schedule after a lag or holiday shift.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FlagIcon />}
                  onClick={handleSetBaseline}
                  disabled={setBaseline.isPending || tracks.length === 0}
                >
                  {baselineSet ? 'Re-set baseline' : 'Set baseline'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {confirmRebaseline && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" gap={1}>
                <Button size="small" color="warning" onClick={handleSetBaseline}>
                  Overwrite
                </Button>
                <Button size="small" onClick={() => setConfirmRebaseline(false)}>
                  Keep current
                </Button>
              </Stack>
            }
          >
            A baseline already exists. Re-setting it overwrites the snapshot and the ghost bars
            will show drift against the NEW schedule, not the original one.
          </Alert>
        )}

        <BackupsDialog projectId={projectId} open={backupsOpen} onClose={() => setBackupsOpen(false)} />

        {trackTypes.length === 0 ? (
          <Alert severity="info" sx={{ mb: tracks.length ? 2 : 1 }}>
            No track types yet —{' '}
            <RouterLink to="/settings/track-types">add one in Settings</RouterLink>.
          </Alert>
        ) : (
          <Stack direction="row" gap={1} flexWrap="wrap" mb={addingTrack ? 2 : tracks.length ? 2 : 1}>
            {trackTypes.map((tt) => (
              <Tooltip key={tt.code} title={tt.label.length > LABEL_MAX ? tt.label : ''}>
                <span>
                  <Button
                    size="small"
                    variant={addingTrack === tt.code ? 'contained' : 'outlined'}
                    disabled={existingTrackTypes.has(tt.code)}
                    onClick={() => {
                      setAddingTrack(tt.code);
                      setTrackForm({
                        label: tt.label,
                        unitLabel: tt.defaultUnitLabel,
                        totalUnits: '100',
                        dueDate: '',
                        plannedStart: '',
                        plannedEnd: '',
                      });
                    }}
                  >
                    + {truncate(tt.label)}
                  </Button>
                </span>
              </Tooltip>
            ))}
          </Stack>
        )}

        {addingTrack && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="body2" fontWeight={500} mb={1}>
              New {trackTypes.find((t) => t.code === addingTrack)?.label} track
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start">
              <TextField
                size="small"
                label="Label"
                value={trackForm.label}
                onChange={(e) => setTrackForm((f) => ({ ...f, label: e.target.value }))}
                sx={{ width: 180 }}
              />
              <TextField
                size="small"
                label="Unit"
                value={trackForm.unitLabel}
                onChange={(e) => setTrackForm((f) => ({ ...f, unitLabel: e.target.value }))}
                sx={{ width: 100 }}
              />
              <TextField
                size="small"
                label="Total units"
                type="number"
                value={trackForm.totalUnits}
                onChange={(e) => setTrackForm((f) => ({ ...f, totalUnits: e.target.value }))}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Due date"
                type="date"
                value={trackForm.dueDate}
                onChange={(e) => setTrackForm((f) => ({ ...f, dueDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label="Planned start"
                type="date"
                value={trackForm.plannedStart}
                onChange={(e) => setTrackForm((f) => ({ ...f, plannedStart: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label="Planned end"
                type="date"
                value={trackForm.plannedEnd}
                onChange={(e) => setTrackForm((f) => ({ ...f, plannedEnd: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                error={Boolean(
                  trackForm.plannedStart && trackForm.plannedEnd && trackForm.plannedEnd < trackForm.plannedStart,
                )}
                helperText={
                  trackForm.plannedStart && trackForm.plannedEnd && trackForm.plannedEnd < trackForm.plannedStart
                    ? 'must be ≥ start'
                    : undefined
                }
                sx={{ width: 160 }}
              />
              <Stack direction="row" gap={1} alignSelf="center">
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleCreateTrack}
                  disabled={
                    createTrack.isPending ||
                    !trackForm.label ||
                    // planned dates: both set or both empty, and ordered
                    Boolean(trackForm.plannedStart) !== Boolean(trackForm.plannedEnd) ||
                    Boolean(
                      trackForm.plannedStart &&
                        trackForm.plannedEnd &&
                        trackForm.plannedEnd < trackForm.plannedStart,
                    )
                  }
                >
                  Create
                </Button>
                <Button size="small" onClick={() => setAddingTrack(null)}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {tracks.length === 0 && !addingTrack && (
          <Typography color="text.secondary" variant="body2">
            No tracks yet. Click a type button above to add one.
          </Typography>
        )}

        {tracks.map((track) => {
          const hasItems = (track.items ?? []).length > 0;
          const pct =
            track.progressPct ??
            (track.totalUnits > 0 ? Math.round((track.currentUnits / track.totalUnits) * 100) : 0);
          return (
          <Accordion key={track.id} disableGutters variant="outlined" sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" pr={1}>
                <Stack direction="row" gap={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>{track.label}</Typography>
                  <Chip size="small" label={track.type} variant="outlined" />
                  {/* Planned size (deliverable count) is the track's own total; progress is the
                      sub-track roll-up when it has any, so the chip never shows a stale counter. */}
                  <Chip size="small" label={`${track.totalUnits} ${track.unitLabel}`} />
                  <Chip
                    size="small"
                    variant="outlined"
                    color={pct === 100 ? 'success' : 'default'}
                    label={hasItems ? `${pct}% · ${track.items!.length} sub` : `${pct}%`}
                  />
                  <Chip
                    size="small"
                    label={humanizeStatus(track.computedStatus ?? track.status)}
                    color={statusColor(track.computedStatus ?? track.status)}
                    variant="outlined"
                  />
                  {track.dueDate && (
                    <Typography variant="caption" color="text.secondary">due {track.dueDate}</Typography>
                  )}
                  {track.plannedStart && track.plannedEnd && (
                    <Typography variant="caption" color="text.secondary">
                      plan {formatDate(track.plannedStart)} – {formatDate(track.plannedEnd)}
                    </Typography>
                  )}
                </Stack>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); deleteTrack.mutate(track.id); }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <TrackDatesRow
                key={`dates-${track.id}-${track.plannedStart ?? ''}-${track.plannedEnd ?? ''}`}
                track={track}
                saving={updateTrack.isPending}
                onSave={async (body) => {
                  try {
                    await updateTrack.mutateAsync({ id: track.id, body });
                  } catch (e) {
                    setErrorMsg((e as Error).message ?? 'Failed to save track dates');
                  }
                }}
              />
              <Stack gap={0.5} mb={1}>
                {(track.items ?? []).length === 0 && (
                  <Typography color="text.secondary" variant="body2">No items yet.</Typography>
                )}
                {[...(track.items ?? [])]
                  .sort((a, b) => a.sequence - b.sequence)
                  .map((item) => (
                    <Stack key={item.id} gap={0.25} mb={0.5}>
                      <PlanItemRow
                        item={item}
                        unitLabel={track.unitLabel}
                        saving={updateItemPlan.isPending}
                        onSave={async (body) => {
                          try {
                            await updateItemPlan.mutateAsync({ itemId: item.id, body });
                          } catch (e) {
                            setErrorMsg((e as Error).message ?? 'Failed to save plan dates');
                          }
                        }}
                      />
                      <PredecessorsCell
                        itemId={item.id}
                        allItems={allItems}
                        dependencies={dependencies}
                        busy={addDependency.isPending || updateDependencyLag.isPending || removeDependency.isPending}
                        onAdd={(predecessorId) => void handleAddPredecessor(item.id, predecessorId)}
                        onUpdateLag={async (id, lagDays) => {
                          try {
                            await updateDependencyLag.mutateAsync({ id, lagDays });
                          } catch (e) {
                            setErrorMsg((e as Error).message ?? 'Failed to update lag');
                          }
                        }}
                        onRemove={(id) => removeDependency.mutate(id)}
                      />
                    </Stack>
                  ))}
              </Stack>

              {addingItem === track.id ? (
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <TextField
                    size="small"
                    label="Item name"
                    value={itemForm.name}
                    onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))}
                    sx={{ width: 220 }}
                    autoFocus
                  />
                  <TextField
                    size="small"
                    label="Units"
                    type="number"
                    value={itemForm.totalUnits}
                    onChange={(e) => setItemForm((f) => ({ ...f, totalUnits: e.target.value }))}
                    sx={{ width: 100 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleAddItem(track.id)}
                    disabled={addTrackItem.isPending || !itemForm.name}
                  >
                    Add
                  </Button>
                  <Button size="small" onClick={() => setAddingItem(null)}>Cancel</Button>
                </Stack>
              ) : (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => { setAddingItem(track.id); setItemForm({ name: '', totalUnits: '100' }); }}
                >
                  Add item
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
          );
        })}
      </Paper>

      {/* Delivery milestones (execution gates), linkable to payment terms */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>Delivery milestones</Typography>
          {!showMsForm && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setShowMsForm(true)}>
              Add milestone
            </Button>
          )}
        </Stack>

        {deliveryMilestones.length === 0 && !showMsForm && (
          <Typography color="text.secondary" variant="body2" mb={1}>
            No milestones yet. Link a gate to a payment term and completing it marks that term
            ready to invoice.
          </Typography>
        )}

        <Stack gap={0.5} mb={showMsForm ? 2 : 0}>
          {deliveryMilestones.map((m) => {
            const display = m.computedStatus ?? m.status;
            const { date: msDate, movedFrom } = milestoneEffectiveDate(m);
            return (
              <Stack key={m.id} direction="row" justifyContent="space-between" alignItems="center" py={0.25}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">{m.name}</Typography>
                  <Tooltip title={movedFrom ? `Moved by the schedule from ${formatDate(movedFrom)}` : ''}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(msDate)}
                      {movedFrom ? ` · moved from ${formatDate(movedFrom)}` : ''}
                    </Typography>
                  </Tooltip>
                  <Chip
                    size="small"
                    label={humanizeStatus(display)}
                    color={statusColor(display)}
                    variant="outlined"
                  />
                </Stack>
                <Stack direction="row" gap={0.5}>
                  {!m.completedDate && (
                    <Button
                      size="small"
                      startIcon={<CheckIcon />}
                      onClick={() => handleCompleteGate(m.id)}
                      disabled={updateDeliveryMilestone.isPending}
                    >
                      Complete
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    aria-label="Delete milestone"
                    onClick={() =>
                      deleteMilestone.mutate(m.id, {
                        onError: (e) => setErrorMsg((e as Error).message ?? 'Failed to delete milestone'),
                      })
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            );
          })}
        </Stack>

        {showMsForm && (
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              label="Milestone name"
              value={msForm.name}
              onChange={(e) => setMsForm((f) => ({ ...f, name: e.target.value }))}
              sx={{ width: 240 }}
              autoFocus
            />
            <TextField
              size="small"
              label="Due date"
              type="date"
              value={msForm.dueDate}
              onChange={(e) => setMsForm((f) => ({ ...f, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleCreateMilestone}
              disabled={createMilestone.isPending || !msForm.name || !msForm.dueDate}
            >
              Add
            </Button>
            <Button size="small" onClick={() => setShowMsForm(false)}>Cancel</Button>
          </Stack>
        )}
      </Paper>

      {/* Milestone schedule links — wire milestones into the timeline */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <MilestoneLinksSection
          projectId={projectId}
          tracks={tracks}
          milestones={deliveryMilestones}
          onError={setErrorMsg}
        />
      </Paper>

      {/* Holidays */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <HolidaySection projectId={projectId} />
      </Paper>

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={8000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(okMsg)}
        autoHideDuration={4000}
        onClose={() => setOkMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setOkMsg(null)} sx={{ width: '100%' }}>
          {okMsg}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

/**
 * Inline predecessors editor on each item row (W4): chips for existing
 * dependencies (click → lag popover, x → remove) plus a searchable
 * autocomplete to add one. Replaces the old bottom-of-tab form, so the link
 * lives next to the row it affects. Cycle rejections surface via the parent's
 * snackbar with the offending chain.
 */
export function PredecessorsCell({
  itemId,
  allItems,
  dependencies,
  busy,
  onAdd,
  onUpdateLag,
  onRemove,
}: {
  itemId: string;
  allItems: { id: string; label: string }[];
  dependencies: TrackItemDependency[];
  busy: boolean;
  onAdd: (predecessorId: string) => void;
  onUpdateLag: (depId: string, lagDays: number) => void;
  onRemove: (depId: string) => void;
}) {
  const preds = dependencies.filter((d) => d.successorId === itemId);
  const linked = new Set(preds.map((p) => p.predecessorId));
  const options = allItems.filter((i) => i.id !== itemId && !linked.has(i.id));
  const label = (id: string) => allItems.find((i) => i.id === id)?.label ?? '(deleted item)';

  const [lagAnchor, setLagAnchor] = useState<{ el: HTMLElement; dep: TrackItemDependency } | null>(null);
  const [lagDraft, setLagDraft] = useState('0');

  if (allItems.length < 2) return null;

  return (
    <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap" pl={4} data-testid={`preds-${itemId}`}>
      <Typography variant="caption" color="text.secondary">
        after
      </Typography>
      {preds.map((d) => (
        <Tooltip key={d.id} title="Click to edit lag">
          <Chip
            size="small"
            variant="outlined"
            label={`${label(d.predecessorId)}${d.lagDays !== 0 ? ` ${d.lagDays > 0 ? `+${d.lagDays}d` : `${d.lagDays}d`}` : ''}`}
            onClick={(e) => {
              setLagAnchor({ el: e.currentTarget, dep: d });
              setLagDraft(String(d.lagDays));
            }}
            onDelete={() => onRemove(d.id)}
            disabled={busy}
          />
        </Tooltip>
      ))}
      <Autocomplete
        key={preds.length /* reset the input after each add */}
        size="small"
        options={options}
        getOptionLabel={(o) => o.label}
        value={null}
        onChange={(_e, v) => v && onAdd(v.id)}
        blurOnSelect
        disabled={busy || options.length === 0}
        renderInput={(params) => (
          <TextField {...params} placeholder={preds.length ? 'add…' : 'add predecessor…'} variant="standard" />
        )}
        sx={{ minWidth: 180 }}
      />

      <Popover
        open={Boolean(lagAnchor)}
        anchorEl={lagAnchor?.el}
        onClose={() => setLagAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack direction="row" gap={1} p={1.5} alignItems="center">
          <TextField
            size="small"
            type="number"
            label="Lag (days)"
            helperText="− = lead/overlap"
            value={lagDraft}
            onChange={(e) => setLagDraft(e.target.value)}
            autoFocus
            sx={{ width: 130 }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={busy || !Number.isInteger(parseInt(lagDraft, 10))}
            onClick={() => {
              if (lagAnchor) onUpdateLag(lagAnchor.dep.id, parseInt(lagDraft, 10) || 0);
              setLagAnchor(null);
            }}
          >
            Save
          </Button>
        </Stack>
      </Popover>
    </Stack>
  );
}

/**
 * Track-level planned dates (W3): the track's own target span. Gives the track
 * a Gantt bar even when none of its items are dated, and anchors the progress
 * chart's target ramp. Same both-or-neither + ordering rules as item plans.
 */
function TrackDatesRow({
  track,
  saving,
  onSave,
}: {
  track: { plannedStart?: string | null; plannedEnd?: string | null };
  saving: boolean;
  onSave: (body: { plannedStart: string | null; plannedEnd: string | null }) => void;
}) {
  const [start, setStart] = useState(track.plannedStart ?? '');
  const [end, setEnd] = useState(track.plannedEnd ?? '');

  const dirty = start !== (track.plannedStart ?? '') || end !== (track.plannedEnd ?? '');
  const consistent = (Boolean(start) && Boolean(end)) || (!start && !end);
  const ordered = !start || !end || end >= start;
  const canSave = dirty && consistent && ordered;

  return (
    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mb={1.5}>
      <Tooltip title="Track-level target span — draws the track's bar on the timeline even without dated items, and sets the target line on the progress chart">
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
          Track plan
        </Typography>
      </Tooltip>
      <TextField
        size="small"
        type="date"
        label="Planned start"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ width: 160 }}
      />
      <TextField
        size="small"
        type="date"
        label="Planned end"
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        InputLabelProps={{ shrink: true }}
        error={Boolean(start && end && !ordered)}
        helperText={start && end && !ordered ? 'must be ≥ start' : undefined}
        sx={{ width: 160 }}
      />
      {dirty && (
        <Button
          size="small"
          variant="contained"
          disabled={!canSave || saving}
          onClick={() => onSave({ plannedStart: start || null, plannedEnd: end || null })}
        >
          Save
        </Button>
      )}
    </Stack>
  );
}

/**
 * One track-item row: name + units + planned start/end date inputs. Only fires
 * the save when the pair is consistent — both dates filled or both cleared —
 * matching the backend's "unplanned until both set" rule.
 */
function PlanItemRow({
  item,
  unitLabel,
  saving,
  onSave,
}: {
  item: TrackItem;
  unitLabel: string;
  saving: boolean;
  onSave: (body: { plannedStart: string | null; plannedEnd: string | null }) => void;
}) {
  const [start, setStart] = useState(item.plannedStart ?? '');
  const [end, setEnd] = useState(item.plannedEnd ?? '');

  const dirty = start !== (item.plannedStart ?? '') || end !== (item.plannedEnd ?? '');
  const consistent = (Boolean(start) && Boolean(end)) || (!start && !end);
  const ordered = !start || !end || end >= start;
  const canSave = dirty && consistent && ordered;

  return (
    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
      <Typography variant="body2" sx={{ minWidth: 24, color: 'text.secondary' }}>
        {item.sequence}.
      </Typography>
      <Typography variant="body2" sx={{ minWidth: 120 }}>{item.name}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
        ({item.totalUnits} {unitLabel})
      </Typography>
      <TextField
        size="small"
        type="date"
        label="Planned start"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ width: 160 }}
      />
      <TextField
        size="small"
        type="date"
        label="Planned end"
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        InputLabelProps={{ shrink: true }}
        error={Boolean(start && end && !ordered)}
        helperText={start && end && !ordered ? 'must be ≥ start' : undefined}
        sx={{ width: 160 }}
      />
      {item.scheduledStart && item.scheduledEnd && (
        <Tooltip title="Engine-derived from dependencies and holidays; read-only">
          <Typography variant="caption" color="text.secondary">
            scheduled {formatDate(item.scheduledStart)} – {formatDate(item.scheduledEnd)}
          </Typography>
        </Tooltip>
      )}
      {dirty && (
        <Button
          size="small"
          variant="contained"
          disabled={!canSave || saving}
          onClick={() => onSave({ plannedStart: start || null, plannedEnd: end || null })}
        >
          Save
        </Button>
      )}
    </Stack>
  );
}

interface NodeOption {
  kind: ScheduleNodeKind;
  id: string;
  label: string;
}

/**
 * Wire delivery milestones into the scheduling graph (decision: "graph wins when
 * linked"). Each polymorphic edge connects two nodes — a sub-track item or a
 * milestone — in either direction; moving one date then slides the dependent
 * ones server-side, and the timeline re-renders the cascade via query
 * invalidation. (Drag-to-link on the SVG is a later enhancement; this explicit
 * editor delivers the same create/delete behaviour today.)
 */
function MilestoneLinksSection({
  projectId,
  tracks,
  milestones,
  onError,
}: {
  projectId: string;
  tracks: ProjectTrack[];
  milestones: ProjectDeliveryMilestone[];
  onError: (msg: string) => void;
}) {
  const { data: links = [] } = useMilestoneLinks(projectId);
  const createLink = useCreateMilestoneLink(projectId);
  const updateLag = useUpdateMilestoneLinkLag(projectId);
  const removeLink = useRemoveMilestoneLink(projectId);

  const [pred, setPred] = useState<NodeOption | null>(null);
  const [succ, setSucc] = useState<NodeOption | null>(null);
  const [lag, setLag] = useState('0');
  const [lagAnchor, setLagAnchor] = useState<{ el: HTMLElement; link: ScheduleDependency } | null>(null);
  const [lagDraft, setLagDraft] = useState('0');

  const nodeOptions: NodeOption[] = [
    ...tracks.flatMap((t) =>
      [...(t.items ?? [])]
        .sort((a, b) => a.sequence - b.sequence)
        .map((i) => ({ kind: 'item' as const, id: i.id, label: `${t.label} › ${i.name}` })),
    ),
    ...milestones.map((m) => ({ kind: 'milestone' as const, id: m.id, label: `◆ ${m.name}` })),
  ];
  const labelOf = (kind: ScheduleNodeKind, id: string) =>
    nodeOptions.find((o) => o.kind === kind && o.id === id)?.label ?? '(deleted node)';

  const handleAdd = async () => {
    if (!pred || !succ) return;
    try {
      await createLink.mutateAsync({
        predecessor: { kind: pred.kind, id: pred.id },
        successor: { kind: succ.kind, id: succ.id },
        lagDays: parseInt(lag, 10) || 0,
      });
      setPred(null);
      setSucc(null);
      setLag('0');
    } catch (e) {
      onError((e as Error).message ?? 'Failed to create link');
    }
  };

  const busy = createLink.isPending || updateLag.isPending || removeLink.isPending;

  return (
    <Box>
      <Stack direction="row" gap={1} alignItems="center" mb={0.5}>
        <Typography variant="subtitle1" fontWeight={600}>
          Milestone links
        </Typography>
        <Tooltip title="Link a milestone to a sub-track (or another milestone). A linked milestone is scheduled from its predecessors — the graph wins, so it can land before or after its typed due date. Moving one date slides the rest.">
          <Chip size="small" variant="outlined" label="how do these work?" />
        </Tooltip>
      </Stack>

      {links.length === 0 ? (
        <Typography color="text.secondary" variant="body2" mb={1.5}>
          No links yet. Connect a milestone to a sub-track so its date drives — or is driven by — the schedule.
        </Typography>
      ) : (
        <Stack gap={0.5} mb={1.5}>
          {links.map((l) => (
            <Stack key={l.id} direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">{labelOf(l.predecessorKind, l.predecessorId)}</Typography>
              <Typography variant="caption" color="text.secondary">→</Typography>
              <Typography variant="body2">{labelOf(l.successorKind, l.successorId)}</Typography>
              <Tooltip title="Click to edit lag">
                <Chip
                  size="small"
                  variant="outlined"
                  label={l.lagDays !== 0 ? (l.lagDays > 0 ? `+${l.lagDays}d` : `${l.lagDays}d`) : 'no lag'}
                  onClick={(e) => {
                    setLagAnchor({ el: e.currentTarget, link: l });
                    setLagDraft(String(l.lagDays));
                  }}
                  disabled={busy}
                />
              </Tooltip>
              <Tooltip title="Remove link">
                {/* span so the Tooltip still gets events while the button is disabled */}
                <span>
                  <IconButton
                    size="small"
                    aria-label="Remove link"
                    disabled={busy}
                    onClick={() =>
                      removeLink.mutate(l.id, {
                        onError: (e) => onError((e as Error).message ?? 'Failed to remove link'),
                      })
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}

      {nodeOptions.length < 2 ? (
        <Typography color="text.secondary" variant="body2">
          Add at least two nodes (sub-tracks and/or milestones) to create a link.
        </Typography>
      ) : (
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Autocomplete
            size="small"
            options={nodeOptions.filter((o) => !(succ && o.kind === succ.kind && o.id === succ.id))}
            getOptionLabel={(o) => o.label}
            value={pred}
            onChange={(_e, v) => setPred(v)}
            isOptionEqualToValue={(a, b) => a.kind === b.kind && a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Predecessor (finishes first)" />}
            sx={{ minWidth: 240 }}
          />
          <Typography variant="body2" color="text.secondary">→</Typography>
          <Autocomplete
            size="small"
            options={nodeOptions.filter((o) => !(pred && o.kind === pred.kind && o.id === pred.id))}
            getOptionLabel={(o) => o.label}
            value={succ}
            onChange={(_e, v) => setSucc(v)}
            isOptionEqualToValue={(a, b) => a.kind === b.kind && a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Successor (then this)" />}
            sx={{ minWidth: 240 }}
          />
          <TextField
            size="small"
            type="number"
            label="Lag (days)"
            helperText="− = overlap"
            value={lag}
            onChange={(e) => setLag(e.target.value)}
            sx={{ width: 120 }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={handleAdd}
            disabled={busy || !pred || !succ}
          >
            Add link
          </Button>
        </Stack>
      )}

      <Popover
        open={Boolean(lagAnchor)}
        anchorEl={lagAnchor?.el}
        onClose={() => setLagAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack direction="row" gap={1} p={1.5} alignItems="center">
          <TextField
            size="small"
            type="number"
            label="Lag (days)"
            helperText="− = lead/overlap"
            value={lagDraft}
            onChange={(e) => setLagDraft(e.target.value)}
            autoFocus
            sx={{ width: 130 }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={busy || !Number.isInteger(parseInt(lagDraft, 10))}
            onClick={() => {
              if (lagAnchor)
                updateLag.mutate(
                  { id: lagAnchor.link.id, lagDays: parseInt(lagDraft, 10) || 0 },
                  { onError: (e) => onError((e as Error).message ?? 'Failed to update lag') },
                );
              setLagAnchor(null);
            }}
          >
            Save
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
}

/** Per-project non-working days; each change recomputes the schedule server-side. */
function HolidaySection({ projectId }: { projectId: string }) {
  const { data: schedule } = useSchedule(projectId);
  const { data: holidays = [] } = useHolidays(projectId);
  const addHoliday = useAddHoliday(projectId);
  const removeHoliday = useRemoveHoliday(projectId);
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');

  const scheduledStarts = (schedule?.items ?? []).map((i) => i.scheduledStart);
  const windowStart = scheduledStarts.length
    ? scheduledStarts.reduce((a, b) => (a < b ? a : b))
    : null;
  const windowEnd = schedule?.projectFinish ?? null;

  const isOutside = (d: string) =>
    !windowStart || !windowEnd || d < windowStart || d >= windowEnd;

  const onAdd = () => {
    if (!date) return;
    addHoliday.mutate(
      { holidayDate: date, label: label.trim() || null },
      { onSuccess: () => { setDate(''); setLabel(''); } },
    );
  };

  const pendingOutside = date !== '' && isOutside(date);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        Non-working days
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {windowStart && windowEnd
          ? `Affects the schedule only between ${formatDate(windowStart)} and ${formatDate(windowEnd)}. Weekends are working days unless marked.`
          : 'No scheduled tasks yet — add planned dates first.'}
      </Typography>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mb={holidays.length ? 1.5 : 0}>
        <TextField
          type="date"
          size="small"
          label="Date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Idul Fitri"
        />
        <Button variant="outlined" onClick={onAdd} disabled={!date || addHoliday.isPending}>
          Add holiday
        </Button>
      </Stack>
      {pendingOutside && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          That date is outside the scheduled window — it will be saved but won&apos;t shift any task
          or show on the timeline until work falls on it.
        </Alert>
      )}
      {addHoliday.isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {(addHoliday.error as Error)?.message ?? 'Could not add holiday'}
        </Alert>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap">
        {holidays.map((h) => {
          const outside = isOutside(h.holidayDate);
          const base = h.label ? `${formatDate(h.holidayDate)} · ${h.label}` : formatDate(h.holidayDate);
          return (
            <Chip
              key={h.id}
              label={outside ? `${base} · no effect` : base}
              title={outside ? 'Outside the scheduled window — no effect on the timeline' : undefined}
              onDelete={() => removeHoliday.mutate(h.id)}
              variant="outlined"
              color={outside ? 'default' : 'warning'}
              size="small"
            />
          );
        })}
      </Stack>
    </Box>
  );
}
